import '../../styles/_credit-submission-confirmation-panel.scss';

import { skipToken } from '@reduxjs/toolkit/dist/query';
import React from 'react';

import { CreditMembershipBanner, CreditRecommendation } from '../../datasource/generated';
import { useGetCreditMembershipBannerQuery } from '../../datasource/queries/cmsMessages';
import { useLazyGetProgramDetailsQuery } from '../../datasource/queries/programDetails';
import { useGetYouMayAlsoLikeQuery } from '../../datasource/queries/recommendations';
import { useAppSelector } from '../../hooks';
import { useRequestCredit } from '../../hooks/useRequestCredit';
import { ReactComponent as PLILogo } from '../assets/PLILogo.svg';
import Button from '../Button/Button';

export interface ICreditSubmissionConfimrationPanel {
    isStandalone?: boolean,
}

const openInNewTab = (url?: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
};

interface IProgramDetailsWithSk {
    sk?: number;
}

interface ICreditRecommendationWithVariationPk extends CreditRecommendation {
    variationPk?: string | null;
}

const MembershipBanner = ({ banner }: { banner: CreditMembershipBanner }) => (
    <section className='credit-submission-confirmation-panel__membership'>
        {banner.heading &&
            <span className='credit-submission-confirmation-panel__section-label sentence-label text--medium-grey'>
                {banner.heading}
            </span>
        }
        <a
            className='credit-submission-confirmation-panel__membership-card'
            href={banner.linkUrl ?? undefined}
            target='_blank'
            rel='noopener noreferrer'
            style={banner.imageUrl ? { backgroundImage: `url("${banner.imageUrl}")` } : undefined}
        >
            <div className='credit-submission-confirmation-panel__membership-content'>
                <PLILogo className='credit-submission-confirmation-panel__membership-logo' width='46' height={null as unknown as undefined} />
                {banner.text &&
                    <span className='credit-submission-confirmation-panel__membership-body paragraph-2 text--medium-grey'>{banner.text}</span>
                }
            </div>
        </a>
    </section>
);

const RecommendationCard = ({ recommendation }: { recommendation: CreditRecommendation }) => {
    const [getProgramDetails] = useLazyGetProgramDetailsQuery();
    const recommendationWithVariationPk = recommendation as ICreditRecommendationWithVariationPk;

    const handleWatchLaunch = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        if (!recommendationWithVariationPk.variationPk) {
            openInNewTab(recommendation.watchUrl);
            return;
        }

        const launchingTab = window.open('/launching', '_blank');
        if (!launchingTab) {
            return;
        }

        try {
            const programDetails = await getProgramDetails(recommendationWithVariationPk.variationPk).unwrap() as IProgramDetailsWithSk;
            if (!programDetails.sk || programDetails.sk <= 0) {
                launchingTab.close();
                openInNewTab(recommendation.watchUrl);
                return;
            }

            launchingTab.location.href = `/?itemSk=${programDetails.sk}`;
        } catch {
            launchingTab.close();
            openInNewTab(recommendation.watchUrl);
        }
    };

    return (
        <div className='credit-submission-confirmation-panel__card'>
            <a
                className='credit-submission-confirmation-panel__card-image'
                href={recommendation.watchUrl ?? undefined}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={recommendation.title ? `Watch ${recommendation.title}` : 'Watch program'}
                onClick={handleWatchLaunch}
                style={recommendation.imageUrl ? { backgroundImage: `url("${recommendation.imageUrl}")` } : undefined}
            >
                {recommendation.durationLabel &&
                    <span className='credit-submission-confirmation-panel__card-duration sentence-label'>{recommendation.durationLabel}</span>
                }
                <span className='credit-submission-confirmation-panel__card-play' aria-hidden={true}>
                    <i className='fa-kit fa-play' />
                </span>
            </a>
            <a
                className='credit-submission-confirmation-panel__card-body'
                href={recommendation.url ?? undefined}
                target='_blank'
                rel='noopener noreferrer'
            >
                <span className='credit-submission-confirmation-panel__card-title paragraph-2--bold'>{recommendation.title}</span>
                <div className='credit-submission-confirmation-panel__card-meta-group'>
                    {recommendation.datesFormatted &&
                        <span className='credit-submission-confirmation-panel__card-meta sentence-label'>{recommendation.datesFormatted}</span>
                    }
                    {recommendation.creditsText &&
                        <span className='credit-submission-confirmation-panel__card-meta sentence-label'>{recommendation.creditsText}</span>
                    }
                </div>
            </a>
        </div>
    );
};

const RecommendationsCarousel = ({ title, recommendations }: { title: string, recommendations: CreditRecommendation[] }) => (
    <section className='credit-submission-confirmation-panel__recommendations'>
        <span className='credit-submission-confirmation-panel__section-label sentence-label text--medium-grey'>
            {title}
        </span>
        <div className='credit-submission-confirmation-panel__carousel'>
            {recommendations.map((recommendation, index) =>
                <RecommendationCard key={recommendation.url ?? index} recommendation={recommendation} />
            )}
        </div>
    </section>
);

export const CreditSubmissionConfimrationPanel = ({ isStandalone }: ICreditSubmissionConfimrationPanel) => {
    const { messages, isGetSubmissionMessagesError } = useRequestCredit();
    const isPM = useAppSelector((state) => state.user.isPM);
    const isOnDemand = useAppSelector((state) => !state.player.launchDetails.isWebcast);
    const recommendationCode = useAppSelector((state) => state.player.launchDetails.itemPk ?? undefined);
    const className = isStandalone ? '--standalone' : '';

    const canShowCarousel = isPM && isOnDemand && !!recommendationCode;
    const { data: recommendations } = useGetYouMayAlsoLikeQuery(
        canShowCarousel ? (recommendationCode as string) : skipToken);

    const { data: membershipBanner } = useGetCreditMembershipBannerQuery(!isPM ? undefined : skipToken);
    const hasMembershipBanner = !!(membershipBanner && (membershipBanner.text || membershipBanner.imageUrl || membershipBanner.heading || membershipBanner.linkUrl));

    const showRecommendations = canShowCarousel && !!recommendations && recommendations.length > 0;
    const showMembership = !isPM && hasMembershipBanner;
    const showBottomSection = !isGetSubmissionMessagesError && (showRecommendations || showMembership);

    return (
        <div className={`watch-modal__content credit-submission-confirmation-panel${className}`}>
            <div className='credit-submission-confirmation-panel__heading-container'>
                <h3 className='credit-submission-confirmation-panel__title heading-4 mb-0'>
                    <i className='fa-kit fa-checkmark credit-submission-confirmation-panel__title-icon' aria-hidden={true} />
                    {messages.confirmationTitle}
                </h3>
                <div
                    className='credit-submission-confirmation-panel__confirmation-body text--medium-grey paragraph-1 mb-0'
                    dangerouslySetInnerHTML={{ __html: messages.confirmationBody }}
                />
            </div>

            <div className='credit-submission-confirmation-panel__cta-container'>
                <Button
                    label={messages.certificateHelpLabel}
                    aria-label={messages.certificateHelpLabel}
                    buttonClass='credit-submission-confirmation-panel__button credit-submission-confirmation-panel__button--primary paragraph-1--bold'
                    action={() => openInNewTab(messages.certificateHelpUrl)}
                />
                <Button
                    label={messages.viewCertificatesLabel}
                    aria-label={messages.viewCertificatesLabel}
                    buttonClass='credit-submission-confirmation-panel__button paragraph-1--bold'
                    action={() => openInNewTab(messages.viewCertificatesUrl)}
                />
            </div>

            {showBottomSection &&
                <>
                    <hr className='credit-submission-confirmation-panel__divider' />
                    {showRecommendations
                        ? <RecommendationsCarousel title={messages.recommendationsTitle} recommendations={recommendations ?? []} />
                        : <MembershipBanner banner={membershipBanner as CreditMembershipBanner} />
                    }
                </>
            }
        </div>
    );
};

export default CreditSubmissionConfimrationPanel;